/* eslint-disable prettier/prettier */
import styled from '@emotion/styled'
import type { DirectDebitMandate } from 'api/directDebitMandate'
import { DirectDebitType } from 'api/directDebitMandate/mappers'
import dayjs from 'dayjs'
import { useRef, useState } from 'react'
import ReactJson from 'react-json-view'
import { useCancelDirectDebitMandateV4, useResendDirectDebitMandateV4, useSyncDirectDebitMandateV4 } from '../api/react-query'
import {
  type AutogiroApprovalState,
  BackOfficeAutogiroMandateResponseDtoV4,
  BackOfficeAutogiroMandateResponseDtoV4AllOfProviderEnum,
  BackOfficeOverforselsServiceMandateResponseDtoV4,
  BackOfficeSepaDirectDebitMandateResponseDtoV4,
} from '../api/swagger/definitions/backoffice'
import resendIcon24 from '../figma/svgs/IconActivity24'
import iconTrash24 from '../figma/svgs/IconTrash24'
import refreshIcon24 from '../figma/svgs/RefreshIcon24'
import BorderRadius from '../figma/tokens/BorderRadius'
import ColorsDeprecated from '../figma/tokens/ColorsDeprecated'
import Spacings from '../figma/tokens/Spacings'
import { BorderContainer } from '../helpers/CreditOnCardHelpers'
import useMaterialNotification from '../hooks/useMaterialNotification'
import TextKeys from '../libs/TextKeys'
import Button from '../mynt-components/components/Button'
import FigmaBox from '../mynt-components/components/FigmaBox'
import SVGContainer from '../mynt-components/components/SVGContainer'
import { When } from '../mynt-components/components/When'
import { YYYY_MM_DD_HH_mm_ss } from '../tiger/Constants'
import DirectDebitTabStatusBox from './DirectDebitTabStatusBox'
import { ModalAreYouSure, ModalMandateLog } from './Modals'
import TextContainer from './TextContainer'

interface DirectDebitTabProps {
  customerId: string
  mandate: DirectDebitMandate
}

export const DirectDebitTabAutoGiroApproval = ({ mandate, customerId }: DirectDebitTabProps) => {
  const syncMutation = useSyncDirectDebitMandateV4(customerId)
  const cancelMutation = useCancelDirectDebitMandateV4(customerId)
  const resendMutation = useResendDirectDebitMandateV4(customerId)
  const notify = useMaterialNotification()

  const statusAnchorRef = useRef<any>()
  const refreshAnchorRef = useRef<any>()
  const cancelAnchorRef = useRef<any>()
  const resendAnchorRef = useRef<any>()
  const [showModal, setShowModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [showResendModal, setShowResendModal] = useState(false)

  const handleMandateCancel = () => {
    cancelMutation.mutateAsync(mandate.id).then(() => {
      notify(`Cancelled mandate with id ${mandate.id}`)
    })
    setShowCancelModal(false)
  }

  const handleMandateResend = () => {
    resendMutation.mutateAsync(mandate.id).then(() => {
      notify(`Resend mandate with id ${mandate.id}`)
    })
    setShowResendModal(false)
  }

  return (
    <FigmaBox fullWidth onMouseLeave={() => setShowModal(false)}>
      <BorderContainer fullWidth smallSpacing>
        <FigmaBox fullWidth direction="row" justify={'space-between'} gap="8px" align="center" bottom={Spacings.medium}>
          <TextContainer textKey={TextKeys.DirectDebitDetailsHeading} />
          <FigmaBox direction={'row'} gap={Spacings.minimum}>
            <FigmaBox fullWidth ref={cancelAnchorRef}>
              <StyledButton
                size="small"
                variant="secondary"
                loading={cancelMutation.isLoading}
                onClick={() => setShowCancelModal(true)}
                startIcon={<SVGContainer SVG={iconTrash24} svgStyles={{ width: Spacings.small, height: Spacings.small }} />}
              />
            </FigmaBox>
            <FigmaBox fullWidth ref={refreshAnchorRef}>
              <StyledButton
                size="small"
                variant="secondary"
                loading={syncMutation.isLoading}
                onClick={async () => syncMutation.mutateAsync(mandate.id)}
                startIcon={<SVGContainer SVG={refreshIcon24} svgStyles={{ width: Spacings.small, height: Spacings.small }} />}
              />
            </FigmaBox>
            <FigmaBox fullWidth ref={resendAnchorRef}>
              <StyledButton
                size="small"
                variant="secondary"
                loading={resendMutation.isLoading}
                onClick={() => setShowResendModal(true)}
                startIcon={<SVGContainer SVG={resendIcon24} svgStyles={{ width: Spacings.small, height: Spacings.small }} />}
              />
            </FigmaBox>
          </FigmaBox>
        </FigmaBox>

        <FigmaBox direction="column" gap={Spacings.small}>
          <FigmaBox direction="column" gap={Spacings.minimum}>
            <TextContainer textKey={TextKeys.DirectDebitDetailsState} />
            <FigmaBox onMouseEnter={() => setShowModal(true)} ref={statusAnchorRef}>
              <DirectDebitTabStatusBox status={mandate.state as AutogiroApprovalState} />
            </FigmaBox>
          </FigmaBox>

          <FigmaBox direction="column" gap={Spacings.minimum}>
            <TextContainer textKey={TextKeys.DirectDebitDetailsModifiedAt} />
            <TextContainer
              textKey={TextKeys.DirectDebitDetailsModifiedAtValue}
              text={dayjs(+mandate.modifiedAt * 1000).format(YYYY_MM_DD_HH_mm_ss)}
            />
          </FigmaBox>

          <FigmaBox direction="column" gap={Spacings.minimum}>
            <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccount} text="Provider" />
            <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccountValue} text={mandate.provider} />
          </FigmaBox>

          <FigmaBox direction="column" gap={Spacings.minimum}>
            <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccount} text="Type" />
            <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccountValue} text={mandate.type} />
          </FigmaBox>

          <MandateAccount mandate={mandate} />
        </FigmaBox>

        <FigmaBox top={Spacings.small}>
          <ReactJson src={mandate} collapsed />
        </FigmaBox>
      </BorderContainer>

      <ModalMandateLog
        showAltarLink={mandate.provider === BackOfficeAutogiroMandateResponseDtoV4AllOfProviderEnum.ATLAR}
        visible={showModal}
        onClose={() => setShowModal(false)}
        anchor={statusAnchorRef.current}
        log={mandate.log}
      />

      <When is={showCancelModal}>
        <ModalAreYouSure
          headingText="Are you sure you want to CANCEL the mandate?"
          anchor={cancelAnchorRef.current}
          onClose={() => setShowCancelModal(false)}
          onContinue={handleMandateCancel}
        />
      </When>
      <When is={showResendModal}>
        <ModalAreYouSure
          headingText="Are you sure you want to RESEND the mandate?"
          anchor={resendAnchorRef.current}
          onClose={() => setShowResendModal(false)}
          onContinue={handleMandateResend}
        />
      </When>
    </FigmaBox>
  )
}

interface MandateAccountProps {
  mandate: DirectDebitMandate
}

const MandateAccount = ({ mandate }: MandateAccountProps) => {
  switch (mandate.type) {
    case DirectDebitType.AUTOGIRO:
      return <AutogiroMandateAccount bankAccount={mandate.bankAccount} />
    case DirectDebitType.SEPA_DIRECT_DEBIT:
      return <SepaMandateAccount iban={mandate.iban} mandateReference={mandate.mandateReference} signatureDate={mandate.signatureDate} />
    case DirectDebitType.OVERFORSELSSERVICE:
      return <OverforselsServiceAccount bankAccount={mandate.bankAccount} contactInfo={mandate.contactInfo} />
    case DirectDebitType.DIRECT_DEBIT_NORWAY:
      return <NorwegianDirectDebitMandateAccount noBankAccount={mandate.noBankAccount} />
  }
}

interface AutoGiroMandateAccountProps {
  bankAccount: string
}

const AutogiroMandateAccount = ({ bankAccount }: AutoGiroMandateAccountProps) => (
  <FigmaBox direction="column" gap={Spacings.minimum}>
    <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccount} text="Bank account" />
    <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccountValue} text={bankAccount} />
  </FigmaBox>
)

interface NorwegianDirectDebitProps {
  noBankAccount: string
}

const NorwegianDirectDebitMandateAccount = ({ noBankAccount }: NorwegianDirectDebitProps) => (
  <FigmaBox direction="column" gap={Spacings.minimum}>
    <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccount} text="Bank account" />
    <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccountValue} text={noBankAccount} />
  </FigmaBox>
)
interface SepaMandateAccountProps {
  iban: string
  mandateReference: string
  signatureDate: string
}

const SepaMandateAccount = ({ iban, mandateReference, signatureDate }: SepaMandateAccountProps) => (
  <>
    <FigmaBox direction="column" gap={Spacings.minimum}>
      <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccount} text="Iban" />
      <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccountValue} text={iban} />
    </FigmaBox>
    <FigmaBox direction="column" gap={Spacings.minimum}>
      <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccount} text="Mandate Reference" />
      <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccountValue} text={mandateReference} />
    </FigmaBox>
    <FigmaBox direction="column" gap={Spacings.minimum}>
      <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccount} text="Signature Date" />
      <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccountValue} text={signatureDate} />
    </FigmaBox>
  </>
)

interface OverforselsServiceAccountProps {
  bankAccount: string
  contactInfo?: {
    fullName: string
    email: string
    phoneNumber: string
  }
}

const OverforselsServiceAccount = ({ bankAccount, contactInfo }: OverforselsServiceAccountProps) => (
  <>
    <FigmaBox direction="column" gap={Spacings.minimum}>
      <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccount} text="Danish Bank Account" />
      <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccountValue} text={bankAccount} />
    </FigmaBox>

    {contactInfo && (
      <FigmaBox direction="column" gap={Spacings.minimum}>
        <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccount} text="Contact Information" />
        <FigmaBox direction="column" gap={Spacings.minimum} left={Spacings.small}>
          <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccount} text="Name" />
          <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccountValue} text={contactInfo.fullName} />

          <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccount} text="Email" />
          <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccountValue} text={contactInfo.email} />

          <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccount} text="Phone" />
          <TextContainer textKey={TextKeys.DirectDebitDetailsBankAccountValue} text={contactInfo.phoneNumber} />
        </FigmaBox>
      </FigmaBox>
    )}
  </>
)

const StyledButton = styled(Button)`
  width: ${Spacings.medium};
  height: ${Spacings.medium};
  padding: 0;
  border: 1px solid ${ColorsDeprecated.stone};
  border-radius: ${BorderRadius.rounded};
`
